import React, { useState, useEffect } from 'react';
import { Building2, Briefcase, Plus, LogOut, Eye, Users, Loader2, X, CheckCircle, FileText, Calendar, MapPin, DollarSign, Clock, XCircle, UserCheck, MessageSquare, Download, ArrowLeft, Key } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [companyToken, setCompanyToken] = useState(localStorage.getItem('company_token') || '');
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    requirements: '',
    salary_range_min: '',
    salary_range_max: '',
    job_type: 'Full Time',
    location: '',
    vacancies: 1,
    eligible_courses: [],
    min_percentage: '',
    application_deadline: '',
    skills_required: []
  });
  const [skillInput, setSkillInput] = useState('');
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    remarks: '',
    interview_date: '',
    interview_time: '',
    interview_venue: '',
    offer_salary: ''
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Get tenant code from URL path
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    // URL pattern: /tenant-code/company-portal
    if (pathParts.length > 2 && pathParts[2] === 'company-portal') {
      return pathParts[1];
    }
    // If directly accessed /company-portal without tenant code, return null
    if (pathParts[1] === 'company-portal') {
      return null;
    }
    // Try from localStorage
    return localStorage.getItem('company_tenant_code') || null;
  };

  const tenantCode = getTenantCode();

  useEffect(() => {
    if (tenantCode) {
      fetchTenantInfo();
    } else {
      setTenantLoading(false);
    }
    if (companyToken) {
      verifySession();
    }
  }, []);

  const fetchTenantInfo = async () => {
    try {
      setTenantLoading(true);
      const response = await axios.get(`${API_URL}/api/tenants/by-subdomain/${tenantCode}`);
      setTenant(response.data);
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    } finally {
      setTenantLoading(false);
    }
  };

  const handleBackToHome = () => {
    if (tenantCode && tenantCode !== 'company-portal') {
      window.location.href = `/${tenantCode}`;
    } else {
      window.location.href = '/login';
    }
  };

  const verifySession = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/placement/company-portal/verify`, {
        params: { company_token: companyToken }
      });
      if (response.data.valid) {
        setCompany(response.data.company);
        setIsLoggedIn(true);
        fetchJobs();
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/placement/company-login`, credentials);
      const { token, company: companyData } = response.data;
      setCompanyToken(token);
      localStorage.setItem('company_token', token);
      if (tenantCode) {
        localStorage.setItem('company_tenant_code', tenantCode);
      }
      setCompany(companyData);
      setIsLoggedIn(true);
      toast.success('Login successful');
      fetchJobs(token);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const savedTenantCode = localStorage.getItem('company_tenant_code') || tenantCode;
    setCompanyToken('');
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_tenant_code');
    setCompany(null);
    setIsLoggedIn(false);
    setJobs([]);
    setApplications([]);
    // Stay on the same tenant's company portal (refresh to show login form)
    if (savedTenantCode) {
      window.location.href = `/${savedTenantCode}/company-portal`;
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/placement/company-portal/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        params: { company_token: companyToken }
      });
      
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async (token = companyToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/company-portal/jobs`, {
        params: { company_token: token }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchApplications = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/company-portal/applications`, {
        params: { company_token: companyToken, job_id: jobId }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    }
  };

  const handleViewApplications = (job) => {
    setSelectedJob(job);
    fetchApplications(job.id);
    setActiveTab('applications');
  };

  const openApplicationModal = (app) => {
    setSelectedApplication(app);
    setStatusUpdate({
      status: app.status || '',
      remarks: app.remarks || '',
      interview_date: app.interview_date || '',
      interview_time: app.interview_time || '',
      interview_venue: app.interview_venue || '',
      offer_salary: app.offer_salary || ''
    });
    setShowApplicationModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/placement/company-portal/applications/${selectedApplication.id}/status`,
        statusUpdate,
        { params: { company_token: companyToken } }
      );
      toast.success('Application status updated');
      setShowApplicationModal(false);
      if (selectedJob) {
        fetchApplications(selectedJob.id);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...jobForm,
        salary_range_min: jobForm.salary_range_min ? parseFloat(jobForm.salary_range_min) : null,
        salary_range_max: jobForm.salary_range_max ? parseFloat(jobForm.salary_range_max) : null,
        min_percentage: jobForm.min_percentage ? parseFloat(jobForm.min_percentage) : null,
        vacancies: parseInt(jobForm.vacancies)
      };

      await axios.post(
        `${API_URL}/api/placement/company-portal/jobs`,
        payload,
        { params: { company_token: companyToken } }
      );
      toast.success('Job posted successfully! Awaiting admin approval.');
      setShowJobModal(false);
      resetJobForm();
      fetchJobs();
    } catch (error) {
      console.error('Error posting job:', error);
      toast.error(error.response?.data?.detail || 'Failed to post job');
    }
  };

  const resetJobForm = () => {
    setJobForm({
      title: '',
      description: '',
      requirements: '',
      salary_range_min: '',
      salary_range_max: '',
      job_type: 'Full Time',
      location: '',
      vacancies: 1,
      eligible_courses: [],
      min_percentage: '',
      application_deadline: '',
      skills_required: []
    });
    setSkillInput('');
  };

  const addSkill = () => {
    if (skillInput.trim() && !jobForm.skills_required.includes(skillInput.trim())) {
      setJobForm({
        ...jobForm,
        skills_required: [...jobForm.skills_required, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setJobForm({
      ...jobForm,
      skills_required: jobForm.skills_required.filter(s => s !== skill)
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      applied: 'bg-blue-100 text-blue-700',
      shortlisted: 'bg-yellow-100 text-yellow-700',
      interview_scheduled: 'bg-purple-100 text-purple-700',
      selected: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      offer_accepted: 'bg-emerald-100 text-emerald-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Not disclosed';
    const format = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${n?.toLocaleString('en-IN')}`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    return min ? `${format(min)}+` : `Up to ${format(max)}`;
  };

  const statuses = [
    { value: 'applied', label: 'Applied' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'selected', label: 'Selected' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'offer_accepted', label: 'Offer Accepted' }
  ];

  if (loading && !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isLoggedIn) {
    if (tenantLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      );
    }

    // If no tenant code, show tenant code input form
    if (!tenantCode) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-10 w-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Company Portal</h2>
              <p className="text-gray-500 mt-2">Enter institution code to continue</p>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const code = e.target.tenantCode.value.trim().toLowerCase();
              if (code) {
                window.location.href = `/${code}/company-portal`;
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Institution Code</label>
                <input
                  name="tenantCode"
                  type="text"
                  placeholder="e.g., test730"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Contact the institution for their code</p>
              </div>
              <button 
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Continue
              </button>
              <button 
                type="button"
                onClick={() => window.location.href = '/login'} 
                className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Main Login
              </button>
            </form>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            {/* Tenant Logo & Name */}
            {tenant ? (
              <>
                {tenant.logo_url ? (
                  <img 
                    src={tenant.logo_url} 
                    alt={tenant.name} 
                    className="h-16 w-auto object-contain mx-auto mb-4"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Building2 className="h-10 w-10 text-white" />
                  </div>
                )}
                <h2 className="text-lg font-semibold text-gray-700 mb-2">{tenant.name}</h2>
              </>
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Building2 className="h-10 w-10 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900">Company Portal</h1>
            <p className="text-gray-500 mt-2">Post jobs, view applications & hire talent</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="company@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                required
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Login'}
            </button>
          </form>
          <p className="text-center text-sm text-gray-500 mt-6">
            Do not have credentials? Contact the college placement cell.
          </p>
          
          {/* Back to Home */}
          <div className="mt-4 text-center">
            <button 
              onClick={handleBackToHome}
              className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{company?.name}</h1>
                <p className="text-blue-200 text-sm">Company Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold">{jobs.length}</p>
                  <p className="text-blue-200">Jobs Posted</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{jobs.filter(j => j.status === 'active').length}</p>
                  <p className="text-blue-200">Active</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Change Password"
                >
                  <Key className="h-5 w-5" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="bg-white rounded-xl shadow-sm p-1 inline-flex">
            <button
              onClick={() => { setActiveTab('jobs'); setSelectedJob(null); }}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'jobs'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Briefcase className="h-4 w-4 inline mr-2" />
              My Jobs
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'applications'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Applications {selectedJob && `(${selectedJob.title})`}
            </button>
          </div>

          {activeTab === 'jobs' && (
            <button
              onClick={() => setShowJobModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center gap-2 font-medium shadow-md"
            >
              <Plus className="h-5 w-5" /> Post New Job
            </button>
          )}
        </div>

        {activeTab === 'jobs' ? (
          /* Jobs Tab */
          <div className="grid gap-4">
            {jobs.length > 0 ? (
              jobs.map(job => (
                <div key={job.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          job.status === 'active' ? 'bg-green-100 text-green-700' :
                          job.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {job.status?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" /> {job.location || 'Not specified'}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" /> {formatSalary(job.salary_range_min, job.salary_range_max)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" /> {job.vacancies} positions
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" /> {job.job_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right text-sm">
                        <p className="text-blue-600 font-semibold">{job.applications_count || 0} Applications</p>
                        <p className="text-green-600">{job.selected_count || 0} Selected</p>
                      </div>
                      {job.status === 'active' && (
                        <button
                          onClick={() => handleViewApplications(job)}
                          className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium text-sm flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" /> View Applications
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-xl">
                <Briefcase className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Jobs Posted Yet</h3>
                <p className="text-gray-500 mb-4">Post your first job to start receiving applications</p>
                <button
                  onClick={() => setShowJobModal(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium"
                >
                  Post a Job
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Applications Tab */
          <div>
            {selectedJob ? (
              <>
                <div className="bg-blue-50 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-blue-900">Applications for: {selectedJob.title}</h3>
                  <p className="text-blue-700 text-sm">{applications.length} total applications</p>
                </div>

                <div className="space-y-4">
                  {applications.length > 0 ? (
                    applications.map(app => (
                      <div key={app.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                              {app.student_name?.charAt(0) || 'S'}
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{app.student_name}</h4>
                              <p className="text-sm text-gray-500">{app.admission_number}</p>
                              <p className="text-sm text-gray-500">{app.course_name}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                Applied: {formatDate(app.applied_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(app.status)}`}>
                              {app.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            {app.resume_url && (
                              <a
                                href={app.resume_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-600"
                                title="View Resume"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              onClick={() => openApplicationModal(app)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                            >
                              Update Status
                            </button>
                          </div>
                        </div>

                        {app.status === 'interview_scheduled' && app.interview_date && (
                          <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                            <p className="text-sm font-medium text-purple-700">
                              Interview: {app.interview_date} at {app.interview_time || 'TBA'} 
                              {app.interview_venue && ` | Venue: ${app.interview_venue}`}
                            </p>
                          </div>
                        )}

                        {app.cover_letter && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Cover Letter:</span> {app.cover_letter}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl">
                      <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-xl font-semibold text-gray-700">No Applications Yet</h3>
                      <p className="text-gray-500">Students will appear here when they apply</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl">
                <Briefcase className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">Select a Job</h3>
                <p className="text-gray-500">Click "View Applications" on any job to see applicants</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Post New Job</h2>
                <button onClick={() => setShowJobModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handlePostJob} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                  <input
                    type="text"
                    required
                    value={jobForm.title}
                    onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select
                    value={jobForm.job_type}
                    onChange={(e) => setJobForm({ ...jobForm, job_type: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Full Time</option>
                    <option>Part Time</option>
                    <option>Internship</option>
                    <option>Contract</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={jobForm.location}
                    onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Bangalore"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Salary (Annual)</label>
                  <input
                    type="number"
                    value={jobForm.salary_range_min}
                    onChange={(e) => setJobForm({ ...jobForm, salary_range_min: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 400000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Salary (Annual)</label>
                  <input
                    type="number"
                    value={jobForm.salary_range_max}
                    onChange={(e) => setJobForm({ ...jobForm, salary_range_max: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 600000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vacancies</label>
                  <input
                    type="number"
                    min="1"
                    value={jobForm.vacancies}
                    onChange={(e) => setJobForm({ ...jobForm, vacancies: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
                  <input
                    type="date"
                    value={jobForm.application_deadline}
                    onChange={(e) => setJobForm({ ...jobForm, application_deadline: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
                <textarea
                  required
                  rows="3"
                  value={jobForm.description}
                  onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the job role..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                <textarea
                  rows="3"
                  value={jobForm.requirements}
                  onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Qualification requirements..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add skill and press Enter"
                  />
                  <button type="button" onClick={addSkill} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {jobForm.skills_required.map(skill => (
                    <span key={skill} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowJobModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Post Job
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Application Status Update Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Update Application</h2>
              <p className="text-gray-500">{selectedApplication.student_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Status</option>
                  {statuses.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {statusUpdate.status === 'interview_scheduled' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <input
                        type="date"
                        value={statusUpdate.interview_date}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_date: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                      <input
                        type="time"
                        value={statusUpdate.interview_time}
                        onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_time: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
                    <input
                      type="text"
                      value={statusUpdate.interview_venue}
                      onChange={(e) => setStatusUpdate({ ...statusUpdate, interview_venue: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., HR Office, Building A"
                    />
                  </div>
                </>
              )}

              {statusUpdate.status === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offer Salary (Annual)</label>
                  <input
                    type="number"
                    value={statusUpdate.offer_salary}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, offer_salary: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 500000"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows="2"
                  value={statusUpdate.remarks}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, remarks: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowApplicationModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Key className="h-5 w-5" /> Change Password
              </h2>
            </div>
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Current Password *</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Password *</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPortal;
